/**
 * pgc-diag.js — global diagnostics + funnel telemetry.
 *
 * Loaded on every page class (public via footer.php, auth via auth-footer.php,
 * dashboard via dashboard-sidebar.php). Provides:
 *   - window.pgcTrack(type, meta, category) — canonical event emitter into
 *     conversion_events (same transport contract as logFunnelEvent).
 *   - Global JS error capture -> /api/upload-log (was grade-v3-only; every
 *     other page class was blind to broken JS).
 *   - page_perf beacon on first hide — nav timing + LCP + CLS + effectiveType.
 *   - dashboard_view once per /dashboard/* page.
 *   - checkout_cancelled / checkout_return for Stripe return-URL params.
 *   - share_click on result-page share buttons.
 *
 * Every path is wrapped — this file must never break a page or throw.
 */
(function () {
    if (window.__pgcDiagLoaded) return;
    window.__pgcDiagLoaded = true;

    /* ---------------------------------------------------------------- *
     * Canonical event emitter. Mirrors logFunnelEvent's transport:      *
     * URLSearchParams + sendBeacon (WebKit-safe), keepalive fetch       *
     * fallback, XHR last resort. Same two gates: tracking kill switch   *
     * and the webdriver flag so synthetic audits stay out of the table. *
     * ---------------------------------------------------------------- */
    function pgcTrack(eventType, metadata, category) {
        if (!eventType) return;
        if (window.PGC_TRACKING && window.PGC_TRACKING.conversion === false) return;
        if (navigator.webdriver) return;
        // Bot gate — JS-rendering crawlers (AhrefsBot & co) fire the beacon
        // then drop the connection mid-body (Apache AH01095 400s) and their
        // rows pollute conversion_events. Same pattern as the collector's
        // $BOT_RE; log-event.php re-checks it server-side for direct hits.
        if (/bot|crawl|spider|slurp|curl|wget|python|scrapy|headless|phantom|pingdom|uptime|monitor|facebookexternal|bytespider|gptbot|claudebot|petalbot|semrush|ahrefs|mj12|dotbot|rogerbot/i.test(navigator.userAgent || '')) return;
        try {
            var body = new URLSearchParams();
            body.append('event_type', eventType);
            body.append('event_category', category || 'engagement');
            body.append('page_url', window.location.pathname || '');
            body.append('metadata', JSON.stringify(metadata || {}));
            var ok = false;
            try {
                ok = !!(navigator.sendBeacon && navigator.sendBeacon('/api/log-event.php', body));
            } catch (e) { ok = false; }
            if (ok) return;
            if (typeof fetch === 'function') {
                fetch('/api/log-event.php', { method: 'POST', body: body, keepalive: true }).catch(function () {});
            } else {
                var x = new XMLHttpRequest();
                x.open('POST', '/api/log-event.php', true);
                x.send(body);
            }
        } catch (e) {}
    }
    window.pgcTrack = pgcTrack;

    /* ---------------------------------------------------------------- *
     * Global JS error capture -> /api/upload-log. grade-v3.js installs  *
     * its own hook on grade pages with channel 'grade-v3-js'; it sets   *
     * __pgcErrHooked so we stand down there and keep its attribution.   *
     * Dedupes per signature, caps 6/page — a throw-loop must not flood. *
     * ---------------------------------------------------------------- */
    if (!window.__pgcErrHooked) {
        window.__pgcErrHooked = true;
        var _errSeen = {}, _errCount = 0;
        // Cross-origin hosts of scripts on this page — captured lazily on a
        // masked 'Script error.' so the log row names its own suspects.
        function extScriptHosts() {
            var hosts = [];
            try {
                var ss = document.scripts;
                for (var i = 0; i < ss.length && hosts.length < 12; i++) {
                    var s = ss[i].src;
                    if (!s) continue;
                    try {
                        var h = new URL(s, location.href).hostname;
                        // Ad/conversion pixels (X pixel, 2026-10-26) are what
                        // Brave/ad-blockers kill — their masked errors aren't
                        // actionable, so they don't count as suspects.
                        if (/(^|\.)(ads-twitter\.com|analytics\.twitter\.com|t\.co)$/.test(h)) continue;
                        if (h && h !== location.hostname && hosts.indexOf(h) < 0) hosts.push(h);
                    } catch (_) {}
                }
            } catch (_) {}
            return hosts.join(',');
        }
        function pgcReportError(stage, msg, src, line, col, errObj) {
            try {
                if (_errCount >= 6) return;
                // Crypto-wallet browsers (Brave iOS, Opera) inject a provider
                // shim that throws on `window.ethereum.*`; MetaMask/Phantom/
                // Trust inject inpage.js whose rejections ("Failed to connect
                // to MetaMask") surface on our page's listener — a wallet name
                // in the message or chrome-extension:// in the stack/src means
                // extension code, never ours.
                if (/window\.ethereum|metamask|inpage\.js|evmAsk|web3|walletconnect|trust ?wallet|coinbase/i.test(String(msg || '')) ||
                    /chrome-extension:\/\//.test(String((errObj && errObj.stack) || '') + ' ' + String(src || ''))) return;
                // iOS Brave is a fork of Firefox for iOS: both inject a
                // `window.__firefox__` bridge for reader-mode JS, and some
                // Brave builds ship it half-initialized so THEIR injected
                // script throws "undefined is not an object (evaluating
                // 'window.__firefox__.reader')" on every page (#192227,
                // user 5276, Brave/iPhone on the grading page). Injected
                // browser code, never ours.
                if (/__firefox__/i.test(String(msg || ''))) return;
                // CefSharp bots (Outlook SafeLinks and other email link
                // scanners prefetching shared URLs) run the page in an
                // embedded Chromium whose COM bridge loses its objects —
                // "Object Not Found Matching Id:N, MethodName:update,
                // ParamCount:4" is that bridge's rejection signature.
                // Scanner noise, never our code.
                if (/object not found matching id:\d+/i.test(String(msg || ''))) return;
                // Extension content-script injections (Kaspersky-class AV and
                // similar) expose a page-scoped `contentScriptData` global;
                // their own code racing the injection throws
                // "undefined is not an object (evaluating 'contentScriptData.init_ts')"
                // on Safari — surfaces in our listener as a rejection.
                if (/contentscriptdata/i.test(String(msg || ''))) return;
                // Browser-extension messaging noise: `runtime.sendMessage()` /
                // `extension.sendMessage()` / `receiving end does not exist`
                // rejections (e.g. "Invalid call to runtime.sendMessage(). Tab not found.")
                // originate inside third-party browser extensions (password managers,
                // shopping assistants, Safari WebKit extensions), never our code.
                if (/runtime\.sendMessage|extension\.sendMessage|receiving end does not exist/i.test(String(msg || ''))) return;
                // Android WebView JS-bridge noise: an injected Java interface
                // (in-app browsers, barcode/scanner wrappers) whose backing Java
                // object was garbage-collected throws
                // "Error invoking postMessage: Java object is gone".
                // WebView-only, never our code — #186456.
                if (/java object is gone|error invoking postmessage/i.test(String(msg || ''))) return;
                // Third-party injected scripts (Safari web extensions, SEO
                // toolbars, shopping assistants) read page meta tags with no
                // null guard — "TypeError: null is not an object (evaluating
                // 'document.querySelector("meta[property='og:type']").content')".
                // No first-party code uses that selector shape, so any
                // unguarded querySelector("meta[...") throw is external noise
                // (#190275). Pages also emit a minimal OG fallback via
                // x-pixel.php so the read succeeds even on bare heads.
                if (/querySelector\(["']meta\[/i.test(String(msg || ''))) return;
                // Infinite recursion inside INJECTED/eval'd minified code:
                // JS-rendering scraping browsers and VPN/proxy injectors run
                // stealth wrappers as page code, so their frames attribute to
                // the page URL with minifier-short names — e.g.
                // "Gk@…/blog/x:230:383 → Ek@…:230:63 → Gk@…" (#191566/191567,
                // spoofed iPhone OS 27_0_1 + CriOS via Starlink). Signature:
                // RangeError + a <=4-char function name on a frame that does
                // NOT come from a .js file. Our inline scripts are hand-written
                // (no short names) and real recursion inside our minified
                // bundles keeps .js frames, so both still report.
                if (/maximum call stack/i.test(String(msg || ''))) {
                    var stackNoJs = String((errObj && errObj.stack) || '').replace(/\S*\.js[^\s:]*?:\d+:\d+/g, '');
                    if (/(^|\s)[A-Za-z_$][\w$]{0,3}@\S/.test(stackNoJs)) return;
                }
                // Bare "Timeout" rejections with no source, line or stack —
                // WebKit fetch/network timeouts and third-party analytics
                // wrappers reject with exactly this shape, and it surfaces on
                // pages with no async pipeline of ours (blog articles).
                // Nothing to act on: 2026-10-29, iPhone Safari on a blog page.
                if (/^timeout$/i.test(String(msg || '')) && !src && !(errObj && errObj.stack)) return;
                // Bare "invalid origin" rejections, same shape: DuckDuckGo iOS
                // injects ContentScopeScripts + webkit.messageHandlers /
                // postMessage bridge transports (origin-checked) into every
                // page as part of its privacy protections, and their
                // origin-check rejections surface on our unhandledrejection
                // listener. The string appears nowhere in our tree; #194108
                // was an anonymous DDG/7 iPhone on a blog article with empty
                // source/stack. An Error carrying a real stack still reports.
                if (/^invalid origin$/i.test(String(msg || '')) && !src && !(errObj && errObj.stack)) return;
                // 'Script error.' + no filename = the browser masked a
                // cross-origin/injected error (3rd-party script, extension
                // content script, blob/frame). Tag it and list the page's
                // external script hosts — ext empty means extension-injected,
                // which we can't act on, so it isn't reported.
                var masked = /^script error\.?$/i.test(String(msg || '')) && !src ? 1 : 0;
                var ext = masked ? extScriptHosts() : '';
                if (masked && !ext) return;
                var sig = stage + '|' + String(msg || '').slice(0, 120) + '|' + String(src || '').split('/').pop() + '|' + line;
                if (_errSeen[sig]) return;
                _errSeen[sig] = 1;
                _errCount++;
                var stack = '';
                if (errObj && errObj.stack) stack = String(errObj.stack).slice(0, 500);
                var payload = {
                    stage: stage === 'unhandledrejection' ? 'promise_reject' : 'js_error',
                    error: String(msg || '').slice(0, 200),
                    pageUrl: location.pathname + location.search,
                    extra: {
                        channel: 'pgc-diag',
                        source: String(src || '').slice(0, 200),
                        line: line, col: col, stack: stack,
                        masked: masked,
                        ext: ext,
                        ua: navigator.userAgent.slice(0, 200)
                    }
                };
                var blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
                if (!(navigator.sendBeacon && navigator.sendBeacon('/api/upload-log', blob))) {
                    fetch('/api/upload-log', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                        keepalive: true
                    }).catch(function () {});
                }
            } catch (e) {}
        }
        window.addEventListener('error', function (e) {
            pgcReportError('error', e.message, e.filename, e.lineno, e.colno, e.error);
        });
        window.addEventListener('unhandledrejection', function (e) {
            var r = e.reason || {};
            pgcReportError('unhandledrejection', (r && r.message) || String(r), '', 0, 0, r instanceof Error ? r : null);
        });
    }

    /* ---------------------------------------------------------------- *
     * page_perf — one row per page view, emitted on first hide. Gives   *
     * TTFB/DCL/load/LCP/CLS per route, which nothing else captures — a  *
     * CF-cache or origin regression is otherwise invisible client-side. *
     * Buffered observers so metrics set before load still arrive.       *
     * ---------------------------------------------------------------- */
    var _perfSent = false, _lcp = 0, _cls = 0;
    try {
        if (typeof PerformanceObserver === 'function') {
            try {
                new PerformanceObserver(function (list) {
                    var es = list.getEntries();
                    if (es.length) _lcp = Math.round(es[es.length - 1].startTime);
                }).observe({ type: 'largest-contentful-paint', buffered: true });
            } catch (e) {}
            try {
                new PerformanceObserver(function (list) {
                    list.getEntries().forEach(function (en) {
                        if (!en.hadRecentInput) _cls += en.value;
                    });
                }).observe({ type: 'layout-shift', buffered: true });
            } catch (e) {}
        }
    } catch (e) {}

    function sendPerf() {
        if (_perfSent) return;
        _perfSent = true;
        try {
            var nav = (performance.getEntriesByType && performance.getEntriesByType('navigation') || [])[0] || {};
            var t = performance.timing || {};
            var conn = navigator.connection || {};
            pgcTrack('page_perf', {
                ttfb: nav.responseStart != null ? Math.round(nav.responseStart)
                    : (t.responseStart && t.navigationStart ? t.responseStart - t.navigationStart : ''),
                dcl: nav.domContentLoadedEventEnd != null ? Math.round(nav.domContentLoadedEventEnd) : '',
                load: nav.loadEventEnd != null ? Math.round(nav.loadEventEnd) : '',
                lcp: _lcp,
                cls: Math.round(_cls * 1000) / 1000,
                tx: nav.transferSize != null ? nav.transferSize : '',
                net: conn.effectiveType || '',
                in: window.IS_LOGGED_IN ? 1 : 0
            }, 'session_telemetry');
        } catch (e) {}
    }
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') sendPerf();
    });
    window.addEventListener('pagehide', sendPerf);

    /* ---------------------------------------------------------------- *
     * Auto events: dashboard page views, Stripe return markers, share   *
     * clicks. Deferred to DOMContentLoaded — cheap and non-blocking.    *
     * ---------------------------------------------------------------- */
    function onReady(fn) {
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
        else fn();
    }

    onReady(function () {
        try {
            var path = location.pathname || '';

            // Dashboard is the logged-in product core and previously emitted
            // nothing — one row per page view makes usage/abandonment visible.
            if (path.indexOf('/dashboard') === 0) {
                pgcTrack('dashboard_view', { page: path.slice(0, 120) }, 'engagement');
            }

            // Stripe returns land on arbitrary pages. `canceled=1` is appended
            // by create-checkout-session.php's cancel_url; `success=1` marks a
            // completed checkout return (credits.php verifies the session
            // server-side — this event is the client-side mirror).
            var q = new URLSearchParams(location.search || '');
            if (q.get('canceled') === '1') {
                pgcTrack('checkout_cancelled', { return_to: path.slice(0, 120) }, 'purchase');
            } else if (q.get('success') === '1') {
                pgcTrack('checkout_return', { return_to: path.slice(0, 120), sid: q.get('session_id') ? 1 : 0 }, 'purchase');
            }

            // Owner-side share intent: the rendered share buttons live inside
            // .xim-share-buttons (ximilar-results) — recipient-side
            // share_report_viewed already exists; this is the sharer.
            document.addEventListener('click', function (e) {
                var a = e.target && e.target.closest ? e.target.closest('.xim-share-buttons a, [data-share-cta]') : null;
                if (!a) return;
                var href = a.getAttribute('href') || '';
                var dest = '';
                try { dest = new URL(href, location.href).hostname; } catch (_) { dest = href.slice(0, 60); }
                pgcTrack('share_click', { dest: String(dest).slice(0, 80), kind: a.getAttribute('data-share-cta') || '' }, 'engagement');
            }, true);
        } catch (e) {}
    });
})();
